import { InProductTrialTypeEnum } from '@nirvana/shared/dtos/types/in-product-trials';

export const NavigationConstants = {
  NETWORK: {
    NAME: 'network',
    FILTERS: {
      PROPERTIES: {
        LAST_SYNC: 'specifics.lastSync',
        IS_SERVER: 'specifics.isVirtualServer',
        OS_TYPE: 'specifics.operatingSystem',
        COMPANY: {
          NAME: 'company.name',
          FILTER: 'company.id[exactMatch]',
        },
        NAME: 'name',
      },
    },
    ADDITIONAL_FILTERS: {
      TYPE: {
        PHYSICAL: {
          type: ['computers', 'goldenImages'],
        },
        VIRTUAL: {
          type: ['virtualMachines', 'goldenImages'],
        },
        CONTAINERS: {
          type: ['containers'],
        },
        ALL_MACHINES: {
          type: ['computers', 'virtualMachines', 'goldenImages', 'containers'],
        },
      },
      SECURITY_MANAGEMENT: {
        endpoints: 1,
        exchange: 1,
        superAgents: 1,
        securityServers: 1,
        containerHosts: 1,
      },
      DEPTH: {
        RECURSIVE: {
          // As of Dec 2020, Executive Summary is not recursive - it only shows information for a single
          // selected company. However, in the Network view, we want to go recursively - i.e., maybe
          // the endpoints are grouped in folders - we want to recurse down these nodes. We'll have an
          // additional restriction on the Company column to make sure we don't show information for more than
          // the selected company.
          depth: 1,
        },
      },
    },
    CONTROLLERS: {
      SCAN_TASK: 'Application.modules.network.controller.endpoints.tasks.scanTaskController',
      ISOLATE_TASK: 'Application.modules.network.controller.endpoints.tasks.isolateTaskController',
    },
  },
  NETWORK_V2: {
    NAME: 'networkV2',
  },
  INCIDENTS: {
    NAME: 'edr',
  },
  ALERTS: {
    NAME: 'alerts',
  },
  REPORTS: {
    NAME: 'reports',
    TIME_RANGE_MAP: {
      1: 1,
      7: 3,
      30: 5,
    },
  },
  RISK_MANAGEMENT: {
    NAME: 'riskManagement',
    SUBSECTIONS: {
      MISCONFIGURATIONS: 'findings',
      VULNERABILITIES: 'vulnerabilities',
      USER_BEHAVIOR_RISKS: 'identity-risks',
      USERS: 'identities',
      DEVICES: 'resources',
      COMPANIES_VIEW: 'companies-view',
    },
  },
  TASKS: {
    NAME: 'tasks',
  },
  EASM_ASSETS_INVENTORY: {
    NAME: 'easmAssetsInventory',
  },
  ENDPOINT_DETAILS: {
    TAB_ID: {
      GENERAL: 'network-details-computerTabGeneral',
      POLICY: 'network-details-computerTabPolicy',
    },
  },
  BLOCK_LIST: {
    FORM: 'form',
    HASH_TYPE: {
      SHA256: 1,
      MD5: 2,
    },
  },
  POLICIES: {
    NAME: 'policies',
  },
  MY_COMPANY: {
    NAME: 'myCompany',
    CONTROLLER: 'Application.modules.myCompany.controller.myCompany',
    TABS: {
      COMPANY_DETAILS: 0,
      AUTHENTICATION: 1,
      LICENSING: 2,
      MSP_PRODUCT_TRIAL_HUB: 3,
      EARLY_ACCESS: 4,
    },
  },
  MY_ACCOUNT: {
    NAME: 'myAccount',
    CONTROLLER: 'Application.modules.myAccount.controller.myAccount',
  },
  MY_INTEGRATIONS: {
    NAME: 'myIntegrations',
    CONTROLLER: 'Application.modules.myIntegrations.controller.main',
  },
  WHATS_NEW: {
    NAME: 'whatsNew',
    CONTROLLER: 'Application.modules.whatsNew.controller.whatsNew',
  },
  NOTIFICATIONS: {
    NAME: 'notifications',
    CONTROLLER: 'Application.modules.notifications.controller.notificationsArea',
  },
  IN_PRODUCT_TRIALS: {
    NAME: 'inProductTrials',
    CONTROLLER: 'Application.modules.inProductTrials.controller.inProductTrials',
  },
  HELP_MODE: {
    NAME: 'helpMode',
  },
  SUPPORT: {
    NAME: 'support',
    CONTROLLER: 'Application.modules.support.controller.support',
  },
  CREDENTIALS: {
    NAME: 'credentials',
    CONTROLLER: 'Application.modules.credentials.controller.credentials',
  },
  COMPANIES: {
    NAME: 'companies',
    CONTROLLER: 'Application.modules.companies.controller.main',
  },
  HEALTH_DASHBOARD: {
    NAME: 'healthDashboard',
  },
  CONFIGURATION_PROFILES: {
    NAME: 'configurationProfiles',
  },
  COMPLIANCE: {
    NAME: 'compliance',
  },
  PHASR_RECOMMENTATIONS: {
    NAME: 'phasrRecommendations',
  },
  XDR_SEARCH: {
    NAME: 'xdrSearch'
  }
};

export const NAVIGATION_CLASS = 'has-action';

export const EFFECTS = {
  HOVER: 'drilldown-binder-hover',
  ACTIVE: 'drilldown-binder-active',
  FOCUS: 'drilldown-binder-focus',
};

/**
 * Slug for each product type
 *
 * When adding a new product here, make sure to add it in the console mapping too
 * Once added, the slug should not be modified, since there may be links pointing to it
 */
export const PRODUCTS_HUB_TRIAL_TYPE_SLUGS :Record<InProductTrialTypeEnum, string> = {
  [InProductTrialTypeEnum.NextGen]: 'business-security-premium',
  [InProductTrialTypeEnum.NextGenWithReconfigurePopup]: 'business-security-premium-from-sbs',
  [InProductTrialTypeEnum.Atd]: 'business-security-enterprise',
  [InProductTrialTypeEnum.XdrFull]: 'xdr-full',
  [InProductTrialTypeEnum.XdrSensors]: 'xdr-sensors',
  [InProductTrialTypeEnum.PatchManagement]: 'patch-management',
  [InProductTrialTypeEnum.VolumeEncryption]: 'volume-encryption',
  [InProductTrialTypeEnum.MobileSecurity]: 'mobile-security',
  [InProductTrialTypeEnum.IntegrityMonitor]: 'integrity-monitor',
  [InProductTrialTypeEnum.StorageProtection]: 'storage-protection',
  [InProductTrialTypeEnum.ContainerProtection]: 'container-protection',
  [InProductTrialTypeEnum.MdrEnterprise]: 'mdr-plus',
  [InProductTrialTypeEnum.Cws]: 'cspm',
  [InProductTrialTypeEnum.ThreatIntel]: 'threat-intelligence',
  [InProductTrialTypeEnum.OfficeSecurityPack]: 'business-security',
  [InProductTrialTypeEnum.NadAndEraAddon]: 'network-attack-defense-and-risk-management',
  [InProductTrialTypeEnum.WebAccessAndDeviceControlAddon]: 'web-access-and-device-control',
  [InProductTrialTypeEnum.ComplianceManager]: 'compliance-manager',
}
